<template>
  <q-page class="privacy-page">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <div class="privacy-page__inner">
      <!-- Hero -->
      <header class="privacy-page__hero">
        <KdsEyebrow>{{ t('privacy.eyebrow') }}</KdsEyebrow>
        <h1 class="privacy-page__title">{{ t('privacy.title') }}</h1>
      </header>

      <LegalDocsLayout :anchors="privacyAnchors">
        <!-- Section 1 — Data we collect -->
        <section id="section-1" class="privacy-page__section" data-reveal>
          <h2 class="privacy-page__section-title">{{ t('privacy.section1.title') }}</h2>
          <p
            v-for="(paragraph, i) in tm('privacy.section1.paragraphs')"
            :key="i"
            class="privacy-page__section-body"
          >{{ rt(paragraph) }}</p>
        </section>

        <!-- Section 2 — How we use your data -->
        <section id="section-2" class="privacy-page__section privacy-page__section--bordered" data-reveal>
          <h2 class="privacy-page__section-title">{{ t('privacy.section2.title') }}</h2>
          <p class="privacy-page__section-body">{{ t('privacy.section2.intro') }}</p>
          <ul class="privacy-page__list">
            <li
              v-for="(item, i) in tm('privacy.section2.items')"
              :key="i"
              class="privacy-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
          <p class="privacy-page__section-body">{{ t('privacy.section2.outro') }}</p>
        </section>

        <!-- Section 3 — Data retention -->
        <section id="section-3" class="privacy-page__section privacy-page__section--bordered" data-reveal>
          <h2 class="privacy-page__section-title">{{ t('privacy.section3.title') }}</h2>
          <p class="privacy-page__section-body">{{ t('privacy.section3.intro') }}</p>
          <ul class="privacy-page__list">
            <li
              v-for="(row, i) in tm('privacy.section3.tableRows')"
              :key="i"
              class="privacy-page__list-item"
            >{{ rt(row) }}</li>
          </ul>
          <p class="privacy-page__section-body">{{ t('privacy.section3.outro') }}</p>
        </section>

        <!-- Section 4 — Third-party services -->
        <section id="section-4" class="privacy-page__section privacy-page__section--bordered">
          <h2 class="privacy-page__section-title">{{ t('privacy.section4.title') }}</h2>
          <p class="privacy-page__section-body">{{ t('privacy.section4.intro') }}</p>
          <ul class="privacy-page__list">
            <li
              v-for="(item, i) in tm('privacy.section4.items')"
              :key="i"
              class="privacy-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
          <p class="privacy-page__section-body">{{ t('privacy.section4.outro') }}</p>
        </section>

        <!-- Section 5 — Your rights -->
        <section id="section-5" class="privacy-page__section privacy-page__section--bordered">
          <h2 class="privacy-page__section-title">{{ t('privacy.section5.title') }}</h2>
          <p class="privacy-page__section-body">{{ t('privacy.section5.intro') }}</p>
          <ul class="privacy-page__list">
            <li
              v-for="(item, i) in tm('privacy.section5.items')"
              :key="i"
              class="privacy-page__list-item"
            >{{ rt(item) }}</li>
          </ul>
          <p
            v-for="(paragraph, i) in tm('privacy.section5.paragraphs')"
            :key="i"
            class="privacy-page__section-body"
          >{{ rt(paragraph) }}</p>
        </section>

        <!-- Section 6 — Contact us -->
        <section id="section-6" class="privacy-page__section privacy-page__section--bordered">
          <h2 class="privacy-page__section-title">{{ t('privacy.section6.title') }}</h2>
          <p
            v-for="(paragraph, i) in tm('privacy.section6.paragraphs')"
            :key="i"
            class="privacy-page__section-body"
          >{{ rt(paragraph) }}</p>
        </section>

        <!-- Section 7 — Cookies and local storage -->
        <section id="section-7" class="privacy-page__section privacy-page__section--bordered">
          <h2 class="privacy-page__section-title">{{ t('privacy.section7.title') }}</h2>
          <p class="privacy-page__section-body">{{ t('privacy.section7.body') }}</p>
        </section>
      </LegalDocsLayout>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import LegalDocsLayout from '@/components/static/LegalDocsLayout.vue';
import { useRevealOnScroll } from '@/composables/useRevealOnScroll';
import { useConsent } from '@/composables/useConsent';
import { useSeoMeta } from '@/composables/useSeoMeta';

const { t, tm, rt } = useI18n();
useSeoMeta('privacy');
useRevealOnScroll();
const router = useRouter();
const { openPreferences } = useConsent();

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const privacyAnchors = computed(() => [
  { label: t('privacy.section1.title'), anchor: 'section-1' },
  { label: t('privacy.section2.title'), anchor: 'section-2' },
  { label: t('privacy.section3.title'), anchor: 'section-3' },
  { label: t('privacy.section4.title'), anchor: 'section-4' },
  { label: t('privacy.section5.title'), anchor: 'section-5' },
  { label: t('privacy.section6.title'), anchor: 'section-6' },
  { label: t('privacy.section7.title'), anchor: 'section-7' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};
</script>

<style scoped lang="scss">
.privacy-page {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-7);
  }

  &__hero {
    margin-bottom: var(--space-9);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: 1.18;
    letter-spacing: -0.015em;
    color: var(--ink);
  }

  &__section {
    padding-top: var(--space-9);
    scroll-margin-top: calc(var(--nav-height, 56px) + var(--space-2));
    display: flex;
    flex-direction: column;
    gap: var(--space-5);

    &--bordered {
      margin-top: var(--space-9);
      border-top: var(--border);
    }
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h2);
    line-height: 1.22;
    letter-spacing: -0.012em;
    color: var(--ink);
  }

  &__section-body {
    margin: 0;
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);

    // Long-form text styles
    a {
      color: var(--ink);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  &__list {
    margin: 0;
    padding-left: var(--space-6);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__list-item {
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);
  }

  ul,
  ol {
    padding-left: var(--space-6);
    margin: 0;
    line-height: 1.65;
    color: var(--stone);
    font-size: var(--fs-body);
  }
}
</style>
